<template>
  <div class="wrapper min-h-full" :class="[appClass, { 'bg-[#121419]': isDarkMode, '!h-auto': isHeightAuto, 'margined-screen': rMargin }]" >
    <main-header v-if="!isWebsite && !isSession"></main-header>

    <website-header v-else-if="isWebsite" :isDarkMode="isDarkMode"></website-header>

    <router-view v-if="prefetched"/>

    <!-- hidden dev menu -->
    <hidden-menu v-if="hiddenMenuShown" :default-settings="hidden_settings" @closed="hiddenMenuClosed" @saved="hiddenMenuSaved"/>

    <!-- modal windows -->

    <sheet-modal v-model="showTermsAgreementModal" exact-width="w-[500px]">
      <terms-agreement :signup="true" next-event="post-auth-terms-accepted"></terms-agreement>
    </sheet-modal>
    <sheet-modal v-model="showHostModal" @closed="showHostModal = false" scrollable exact-width="w-[550px]">
      <host-menu @close="showHostModal = false" />
    </sheet-modal>
    <sheet-modal  v-model="showChangePasswordModal" exact-width="w-[400px]" @closed="closeChangePasswordModal">
      <change-password-popup @close="showChangePasswordModal = false" />
    </sheet-modal>

    <sheet-modal v-model="showProfileModal" exact-width="w-[450px]" scrollable>
      <profile-popup @close="showProfileModal = false"/>
    </sheet-modal>
    <sheet-modal v-model="showAccountModal" exact-width="w-[450px]" scrollable>
      <account-popup @close="showAccountModal = false" />
    </sheet-modal>
    <sheet-modal v-model="showSubscriptionModal" exact-width="w-[450px]" scrollable>
      <subscription-popup @close="showSubscriptionModal = false" />
    </sheet-modal>

    <ModalsContainer />

    <!-- Toast notifications -->
    <NotificationGroup group="app" position="bottom">
      <Notification
          v-slot="{ notifications, close }"
          enter="transform ease-out duration-300 transition"
          enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
          enter-to="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-500"
          leave-from="opacity-100"
          leave-to="opacity-0"
          move="transition duration-500"
          move-delay="delay-300"
      >
        <div
            v-if="notifications.length"
            class="fixed inset-x-0 bottom-0 flex items-end justify-end p-6 px-4 py-6 z-[10000]"
        >
          <div class="min-w-66 max-w-sm">
            <div
                class="flex items-center justify-between rounded-lg w-full max-w-sm mx-auto mt-4 font-semibold text-black w-fit mt-4 shadow-md"
                v-for="notification in notifications"
                :key="notification.id"
                :class="notification.meta?.classes"
            >
              <span>{{ notification.text }}</span>
              <button @click="close(notification.id)" class="p-2 cursor-pointer">
                <!-- svg -->
              </button>
            </div>
          </div>
        </div>
      </Notification>
    </NotificationGroup>

    <!-- Alerts -->
    <NotificationGroup group="alerts" position="top">
      <Notification
          v-slot="{ notifications, close }"
          enter="transform ease-out duration-300 transition"
          enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
          enter-to="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-500"
          leave-from="opacity-100"
          leave-to="opacity-0"
          move="transition duration-500"
          move-delay="delay-300"
      >
        <div
            v-if="notifications.length"
            class="fixed inset-x-0 bottom-0 flex items-end justify-end p-6 px-4 py-6 z-[10000]"
        >
          <div class="min-w-66 max-w-sm">
            <div
                class="flex items-center justify-between rounded-lg w-full max-w-sm mx-auto mt-4 font-semibold text-black w-fit mt-4 shadow-md"
                v-for="notification in notifications"
                :key="notification.id"
                :class="notification.meta?.classes"
            >
              <span>{{ notification.text }}</span>
              <button @click="close(notification.id)" class="p-2 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <g clip-path="url(#clip0_13839_19333)">
                    <path fill-rule="evenodd" clip-rule="evenodd"
                          d="M3.5286 3.5286C3.78895 3.26825 4.21105 3.26825 4.4714 3.5286L8 7.05719L11.5286 3.5286C11.7889 3.26825 12.2111 3.26825 12.4714 3.5286C12.7318 3.78895 12.7318 4.21105 12.4714 4.4714L8.94281 8L12.4714 11.5286C12.7318 11.7889 12.7318 12.2111 12.4714 12.4714C12.2111 12.7318 11.7889 12.7318 11.5286 12.4714L8 8.94281L4.4714 12.4714C4.21105 12.7318 3.78895 12.7318 3.5286 12.4714C3.26825 12.2111 3.26825 11.7889 3.5286 11.5286L7.05719 8L3.5286 4.4714C3.26825 4.21105 3.26825 3.78895 3.5286 3.5286Z"
                          fill="black" fill-opacity="0.55"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_13839_19333">
                      <rect width="16" height="16" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </Notification>
    </NotificationGroup>
  </div>
</template>

<script>
import MainHeader from '@/components/MainHeader.vue'
import WebsiteHeader from '@/components/Website/WebsiteHeader.vue'
import HiddenMenu from "@/components/HiddenMenu.vue"
import { mapState } from 'vuex'
import { loadLanguageAsync } from "@/plugins/i18n"
import hotkeys from 'hotkeys-js'
import emitter from "@/services/emitter.service"
import TermsAgreement from "@/components/auth/TermsAgreement.vue"
import { getPendo } from '@/services/pendo.service'
import HostMenu from './components/HostMenu.vue'
import ProfilePopup from './components/account/ProfilePopup.vue'
import AccountPopup from './components/account/AccountPopup.vue'
import SubscriptionPopup from './components/account/SubscriptionPopup.vue'
import ChangePasswordPopup from './components/auth/ChangePasswordPopup.vue';
import SheetModal from '@/components/ui/SheetModal.vue'
import { ModalsContainer } from 'vue-final-modal'

import { NotificationGroup, Notification } from "notiwind"

export default {
  components: {
    MainHeader,
    WebsiteHeader,
    HiddenMenu,
    SheetModal,
    ModalsContainer,
    TermsAgreement,
    HostMenu,
    NotificationGroup,
    Notification,
    ProfilePopup,
    AccountPopup,
    SubscriptionPopup,
    ChangePasswordPopup,
  },

  data() {
    return {
      zoom: 1,
      hiddenMenuShown: false,
      showChangePasswordModal: false,
      showSignInModal: false,
      showRegisterModal: false,
      showEmailConfirmationModal: false,
      showTermsAgreementModal: false,
      showHostModal: false,
      showProfileModal: false,
      showAccountModal: false,
      showSubscriptionModal: false,
      rMargin: false,
    }
  },

  computed: {
    ...mapState('auth', ['agreed', 'authorized', 'token']),
    ...mapState('nonpersist', ['user', 'prefetched']),
    ...mapState('locale', ['availableLocales', 'locale']),
    ...mapState('hidden', ['hidden_settings']),

    appClass() {
      let wrapperClass = 'overlay-page'
      if (this.$route.meta && this.$route.meta.tpl) {
        wrapperClass = this.$route.meta.tpl
      }
      return wrapperClass
    },

    isMobile() {
      return screen.width < 1200
    },

    isWebsite() {
      return this.$route.meta.tpl && this.$route.meta.tpl === 'website'
    },

    // The OBS overlay is a session page too: no app header on it.
    isSession() {
      return this.$route.meta.tpl && ['conference', 'overlay'].includes(this.$route.meta.tpl)
    },

    isDarkMode() {
      return ['plans', 'faq', 'glossary', 'terms', 'payment_success', 'transcripts', 'transcript', 'ai-transcripts', 'ai-transcript', 'share-transcript', 'organization', 'organization-vocabulary', 'icons-docs'].includes(this.$route.name)
    },

    isHeightAuto() {
      return ['plans', 'faq', 'terms', 'icons-docs', 'organization', 'organization-vocabulary', "logs"].includes(this.$route.name)
    }
  },

  methods: {
    initBeaconJS() {
      !function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){})
      window.Beacon('init', process.env.VUE_APP_BEACON_ID)

      // keep the Contact button above the footer text: the footer is one row on desktop
      // and wraps to two rows at 1100px and below (on phones Beacon hides the button itself)
      const twoRowFooter = window.matchMedia('(max-width: 1100px)')
      const setBeaconOffset = () => window.Beacon('config', { display: { verticalOffset: twoRowFooter.matches ? 110 : 60 } })
      setBeaconOffset()
      twoRowFooter.addEventListener('change', setBeaconOffset)
    },

    hiddenMenu() {
      this.hiddenMenuShown = !this.hiddenMenuShown
    },

    hiddenMenuClosed() {
      this.hiddenMenuShown = false
    },

    hiddenMenuSaved() {
      this.hiddenMenuClosed()
      location.reload()
    },

    async handleAuth0Redirect() {
      const normalizeLocale = (locale) => {
        if (!locale) return 'en'
        return locale.split('-')[0]
      }

      await this.$forumAuth.login({
        authorizationParams: {
          ui_locales: normalizeLocale(this.$i18n.locale)
        }
      })
    },

    handleOpenTermsAgreementModal() {
      this.handleAuth0Redirect()
    },

    shouldShowPostAuthTermsModal() {
      if (!this.authorized) {
        return false
      }

      return this.agreed !== true
    },

    evaluatePostAuthTermsModal() {
      this.showTermsAgreementModal = this.shouldShowPostAuthTermsModal()
    },

    handleOpenProfilePopup() {
      this.showProfileModal = true
    },

    handleOpenHostModal() {
      if (this.shouldShowPostAuthTermsModal()) {
        this.showHostModal = false
        this.showTermsAgreementModal = true
        return
      }

      this.showHostModal = true
    },

    handleOpenAccountPopup() {
      this.showAccountModal = true
    },

    handleOpenSubscriptionPopup() {
      this.showSubscriptionModal = true
    },

    handleOpenDrawer(drawerParams) {
      this.rMargin = drawerParams.open
    },

    handleOpenChangePasswordModal() {
      this.showChangePasswordModal = true;
    },

    bindEmitterEvents() {
      emitter.on('open-auth0', this.handleAuth0Redirect)
      emitter.on('open-terms-agreement-modal', this.handleOpenTermsAgreementModal)
      emitter.on('open-host-modal', this.handleOpenHostModal)
      emitter.on('open-profile-popup', this.handleOpenProfilePopup)
      emitter.on('open-account-popup', this.handleOpenAccountPopup)
      emitter.on('open-subscription-popup', this.handleOpenSubscriptionPopup)
      emitter.on('open-change-password-modal', this.handleOpenChangePasswordModal)
      emitter.on('drawer_shift_state', this.handleOpenDrawer)
    },

    unbindEmitterEvents() {
      emitter.off('open-auth0', this.handleAuth0Redirect)
      emitter.off('open-terms-agreement-modal', this.handleOpenTermsAgreementModal)
      emitter.off('open-host-modal', this.handleOpenHostModal)
      emitter.off('open-profile-popup', this.handleOpenProfilePopup)
      emitter.off('open-account-popup', this.handleOpenAccountPopup)
      emitter.off('open-subscription-popup', this.handleOpenSubscriptionPopup)
      emitter.off('drawer_shift_state', this.handleOpenDrawer)
    },
    handleAuth0RedirectMessage() {
      const { message, success, code } = this.$route.query

      if (!message || code !== 'success') {
        return
      }

      const isSuccess = success === 'true' || success === true

      this.$notify(
          {
            group: 'app',
            text: String(message),
            meta: {
              classes: isSuccess
                  ? 'bg-[#DFF7E8] text-[#14532D] px-4 py-3'
                  : 'bg-[#FEE2E2] text-[#7F1D1D] px-4 py-3',
            },
          },
          6000
      )

      const cleanedQuery = { ...this.$route.query }

      delete cleanedQuery.message
      delete cleanedQuery.success
      delete cleanedQuery.code
      delete cleanedQuery.supportSignUp
      delete cleanedQuery.supportForgotPassword

      this.$router.replace({
        path: this.$route.path,
        query: cleanedQuery,
        hash: this.$route.hash,
      })
    },
  },

  async created() {
    this.bindEmitterEvents()

    await loadLanguageAsync(this.$i18n.locale)
    await this.$store.dispatch('lang/get')
    await this.$forumAuth.init()
    this.evaluatePostAuthTermsModal()

    this.$nextTick(() => {
      this.handleAuth0RedirectMessage()
    })
  },

  async mounted() {
    hotkeys('shift+alt+k, shift+command+k', () => this.hiddenMenu())
    this.initBeaconJS()

    const shortLang = this.locale ? this.locale : 'en'

    const pendo = getPendo(this.user)
    if (this.user?.email && pendo) {
      pendo.identify({
        visitor: {
          id: this.user.email,
          forum_language: shortLang,
        },
        account: {}
      })
    }

    if (['faq', 'plans'].includes(this.$route.name)) {
      document.documentElement.style.setProperty('--app-overflow-smallscreen', 'auto')
    }

    if (this.isDarkMode) {
      document.documentElement.style.setProperty('--background-color', '#121419')
    }
  },

  beforeUnmount() {
    this.unbindEmitterEvents()
    hotkeys.unbind('shift+alt+k, shift+command+k')
  },

  watch: {
    '$route': {
      immediate: true,
      handler() {
        if (this.isDarkMode) {
          document.documentElement.style.setProperty('--background-color', '#121419')
        } else {
          document.documentElement.style.setProperty('--background-color', '#ffffff')
        }
      }
    },
    authorized() {
      this.evaluatePostAuthTermsModal()
    },
    agreed() {
      this.evaluatePostAuthTermsModal()
    }
  }
}
</script>

<style lang="scss">
  :root {
    --toggle-width: 47px;
    --toggle-height: 27px;
    --toggle-bg-on: #6CBFFF;
    --toggle-bg-off: #e5e7eb;
    --toggle-border-on: #6CBFFF;
    --toggle-text-off: transparent;
    --toggle-text-on: transparent;

    --popper-theme-background-color: rgba(240, 246, 255, 0.05);
    --popper-theme-background-color-hover: rgba(240, 246, 255, 0.05);
    --popper-theme-text-color: #ffffff;
    --popper-theme-border-width: 1px;
    --popper-theme-border-style: solid;
    --popper-theme-border-color: rgba(240, 246, 255, 0.05);
    --popper-theme-border-radius: 8px;
    --popper-theme-padding: 4px 10px;
    --app-overflow-smallscreen: "auto";
  }

  * {
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', sans-serif;
  }

  a {
    text-decoration: none;
  }

  html, body {
    height: 100%;
  }

  html {
    font-size: 16px;
    background-color: var(--background-color);
  }

  #app {
    height: 100%;

    @media all and (max-width: 1100px) {
      overflow: var(--app-overflow-smallscreen);
    }
  }

  .wrapper {
    display: flex;
    height: 100%;
    flex-direction: column;
    justify-content: space-between;

    &.conference {
      background: #0C1116;

      @media all and (max-width: 800px) {
        background: #12181E;
      }

      .main-header {
        padding: 16px 24px 8px 24px;

        &__logo img {
          width: 114px;
        }
      }
    }
  }

  .flex-centered {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    height: 100%;

    &--home {
      justify-content: center;

      gap: 56px;
    }
  }

  .flex-column {
    display: flex;
    flex-direction: column;
  }

  .flex-row {
    display: flex;
    flex-direction: row;
  }

  .orange-link {
    color: #6CBFFF;
    font-weight: 700;
    font-size: 1.25rem;
    line-height: 1.875rem;
    display: flex;
    align-items: center;

    img {
      margin-right: 15px;
    }

    &.orange-link--inline {
      text-transform: none;
      font-family: 'Inter';
      font-weight: 400;
      justify-content: center;
    }
  }

  .orange-btn {
    min-width: 412px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 700;
    font-size: 1.875rem;
    line-height: 2.8rem;
    text-align: center;
    padding: 0 30px;

    color: #FFFFFF;
    background: #6CBFFF;
    box-shadow: 0px 5.01333px 5.01333px rgba(0, 0, 0, 0.25);
    border-radius: 47.6267px;
    height: 94px;
    cursor: pointer;

    img {
      margin-right: 20px;
    }

    &:hover {
      opacity: 0.8;
    }
  }

  .or-divider {
    text-align: center;
    color: #FFFFFF;
    font-size: 1rem;
    line-height: 1.2rem;
    font-family: 'Inter';
    margin-top: 21px;
    margin-bottom: 14px;
  }

  button:disabled,
  button[disabled] {
    opacity: 0.5;
    touch-action: none;
    pointer-events: none;
    cursor: default;
  }

  .text-page {
    background: #202020;

    .overlay {
      display: none;
    }
  }

  .text-content {
    max-width: 800px;
    margin: 0 auto;
    color: #FFFFFF;
    height: 100%;
  }

  .text-transform-none {
    text-transform: none !important;
  }

  .hidden-radio {
    position: absolute;
    opacity: 0;
  }

  .margined-submit {
    margin-top: 100px;
  }

  .back-arrow-btn {
    color: #6CBFFF;
    font-size: 1rem;
    line-height: 1.18rem;
    text-decoration: none;
    padding-left: 20px;
    background: url(@/assets/icons/arrow-b.svg) left center no-repeat;
  }

  .padded-top-block {
    padding-top: 70px !important;

    @media all and (max-height: 800px) {
      padding-top: 35px !important;
    }
  }

  .centered-buttons {
    display: flex;
    justify-content: center;
    flex-direction: column;

    .back-arrow-btn {
      margin: 30px auto 0;
    }
  }


  .ps__rail-y, .ps__thumb-y {
    z-index: 100;
  }

  .transparenthidden {
    opacity: 0 !important;
  }

  .toggle-container:focus {
    box-shadow: none !important;
    outline: none;
  }

  .danger-btn {
    color: #EA4335;
    text-decoration: none;
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;

    img {
      max-width: 20px;
      margin-right: 10px;
    }
  }

  .languages-scroll {
    height: 450px;
    border: 1px solid #505050;
    border-radius: 10px;
    padding: 0 10px 0 10px;
    background: #1D1C1C;
    position: relative;
  }

  .languages-scroll-light {
    background: #fff;
    border: 2px solid #505050;
    border-radius: 10px;
    height: 400px;

    .root-list__item {
      padding-left: 10px;

      &.all-language {
        padding-left: 16px;
      }
    }
  }

  .profile-scroll{
    height: 450px;
  }

  .scrollbar__thumbPlaceholder {
    right: -20px !important;
  }

  .start-slider img {
    max-width: 110px;
  }

  /** responsive */

  @media all and (max-height: 800px) {

    .start {

      &-slider__item {
        img {
          max-width: 87px;
          margin-bottom: 30px !important;
        }
      }

      &__options {
        margin-top: 40px;
      }
    }

    .margined-submit {
      margin-top: 36px !important;
    }

    .languages-scroll, .profile-scroll {
      height: 350px;
    }
  }

  @media all and (max-width: 1100px) {
    .languages-scroll {
      height: 100% !important;
      border: none;
    }
  }

  .scrollbar__thumb{
    background-color: #aaa !important;
    border-radius: 6px;
    transition: background-color .2s linear, width .2s ease-in-out;
    -webkit-transition: background-color .2s linear, width .2s ease-in-out;
  }

  .conferenceBody #beacon-container {
    display: none !important;
  }

  .conferenceBody {
    background: #0C1116;
  }

  .host .conference-close, .start .conference-close, .profile .conference-close {
    position: absolute;
    top: -7%;
    right: -7%;
  }


</style>
